import { TASK_SOURCE_LABELS, tagKurz, type TaskSource } from '@netcon-work/shared'
import { useState } from 'react'
import { TabellenUmschalter, useHinweis } from './hinweis'

/**
 * Aktivitäts-Zeitleiste: wer war an welchem Tag womit beschäftigt (Kap. 4.6).
 *
 * Optik aus dem Mockup (`.tl-days` / `.tl-row` / `.seg`): Raster aus Personen
 * und Tagen, in jeder Zelle flache farbige Segmente je Quelle.
 *
 * ## Ein Unterschied zum Mockup, den die echten Daten erzwingen
 *
 * Im Mockup ist **ein Segment ein Ereignis** – dort hat jede Person ein bis zwei
 * pro Tag. Im echten Bestand sind es bis zu mehreren Dutzend; als Einzelsegmente
 * wäre die Zelle ein Streifenmuster ohne Aussage.
 *
 * Deshalb ist eine Zelle hier **ein gestapelter Balken**: ein Segment je Quelle,
 * die Gesamtlänge im Verhältnis zum aktivsten Tag des Rasters. Damit liest die
 * Zeile sich als Verlauf („Dienstag war viel, Freitag wenig"), und die Farbe
 * bleibt, was sie im Mockup ist – die Quelle.
 *
 * ## Farbe und Kontrast
 *
 * Quellenfarben sind die Slots 1–3 der Palette und bestehen den All-Pairs-Test
 * (schlechtestes Paar ΔE 9,2 bei Deuteranopie, 24,0 bei Normalsicht). Work
 * (`#1baf7a`) erreicht gegen Weiß aber nur 2,82:1 und liegt damit unter 3:1 –
 * zulässig nur mit zweitem Weg zum Wert. Der ist die **Tabellensicht** oben
 * rechts, dazu Legende und Sprechblase. Farbe ist nie der einzige Kanal.
 *
 * **Aktivität, nicht Ergebnis.** Hier werden Ereignisse gezählt, nicht Aufgaben
 * (anders als „Erledigt pro Tag", siehe E-18): wer eine Aufgabe dreimal anfasst,
 * war dreimal tätig. Und keine Zeiterfassung – die Zelle sagt „an diesem Tag
 * passierte etwas", nicht „so lange wurde gearbeitet".
 *
 * ## Abwesende Tage (Zusatzpaket Z-01)
 *
 * **Hier liegt der größte Nutzen der Abwesenheiten, und er ist ein Schutz vor
 * einer Fehlinterpretation.** Eine leere Zelle heißt „an diesem Tag passierte
 * nichts". Über einem Urlaubstag ist genau das die falsche Lesart: dort *konnte*
 * nichts passieren. Wer eine Zeile mit fünf leeren Tagen sieht und nicht weiß,
 * dass es Urlaub war, liest eine Leistungsaussage in ein Nichts hinein – im
 * Leitstand, der laut Konzept Kap. 4.6 Arbeits-Transparenz und **nicht**
 * Leistungskontrolle sein soll.
 *
 * Markiert wird deshalb mit einer schraffierten Fläche **und** in der Sprechblase
 * mit Text; die Tabellensicht schreibt „ab." statt „–". Drei Wege zum selben
 * Wert, keiner davon nur Farbe.
 */

const QUELLE_FARBE: Record<string, string> = {
  gitlab: 'var(--color-src-gitlab)',
  netcon_ticket: 'var(--color-src-netcon)',
  work: 'var(--color-src-work)',
}

/** Eine geteilte leere Menge – spart je Zeile ein neues Set. */
const LEER: ReadonlySet<string> = new Set<string>()

/**
 * Schraffur für abwesende Tage.
 *
 * Ein Muster und keine Füllfarbe: eine graue Fläche würde wie „wenig Aktivität"
 * aussehen und sich damit in dieselbe Skala einsortieren wie die Balken daneben.
 * Eine Schraffur liest sich als „gilt nicht", nicht als kleiner Wert.
 */
const ABWESEND_MUSTER =
  'repeating-linear-gradient(135deg, var(--color-line2) 0 2px, transparent 2px 5px)'

export type ZeitleistePerson = {
  personId: string
  name: string
  tage: { tag: string; jeQuelle: Record<string, number> }[]
  gesamt: number
}

export function AktivitaetsZeitleiste({
  tage,
  personen,
  abwesendAn,
  onZelleKlick,
}: {
  tage: string[]
  personen: ZeitleistePerson[]
  /**
   * An welchen Tagen war die Person weg (Z-01)? Optional – ohne Abwesenheitsdaten
   * sieht die Zeitleiste aus wie vorher, statt „alle immer da" zu behaupten.
   */
  abwesendAn?: (personId: string, tage: readonly string[]) => Set<string>
  onZelleKlick: (personId: string, name: string, tag: string) => void
}) {
  const { anMark, feld } = useHinweis()
  const [tabelle, setTabelle] = useState(false)
  const abwesend = (personId: string): ReadonlySet<string> =>
    abwesendAn ? abwesendAn(personId, tage) : LEER

  const summe = (jeQuelle: Record<string, number>) =>
    Object.values(jeQuelle).reduce((s, n) => s + n, 0)

  // Bezugsgröße für die Balkenlänge: der aktivste Tag im ganzen Raster. Eine
  // Skalierung je Zeile würde Personen vergleichbar aussehen lassen, die es
  // nicht sind.
  const hoechst = Math.max(1, ...personen.flatMap((p) => p.tage.map((t) => summe(t.jeQuelle))))

  /** Welche Quellen im Zeitraum überhaupt vorkommen – nur die brauchen Legende. */
  const quellen = [
    ...new Set(personen.flatMap((p) => p.tage.flatMap((t) => Object.keys(t.jeQuelle)))),
  ].sort()

  /**
   * **Ohne WIP-Spalte** seit E-358: die Auslastung steht in der Team-Tabelle des Reiters
   * „Heute". Hier war sie die dritte Stelle mit derselben Zahl.
   */
  const spalten = `150px repeat(${tage.length}, 1fr)`
  const kurzeKoepfe = tage.length > 14
  /** War im Zeitraum überhaupt jemand weg? Entscheidet über die Legende. */
  const hatAbwesenheit = personen.some((p) => abwesend(p.personId).size > 0)

  if (personen.length === 0) {
    return (
      <div className="py-2 text-[13px] text-ink2">
        In diesem Zeitraum ist keine Aktivität verzeichnet.
      </div>
    )
  }

  return (
    <>
      <div className="mb-1.5 flex items-baseline">
        <span className="text-[11px] text-muted">
          aus Statuswechseln, Kommentaren und Commits – keine Zeiterfassung
        </span>
        <span className="ml-auto">
          <TabellenUmschalter
            offen={tabelle}
            umschalten={() => setTabelle((o) => !o)}
            diagramm="Aktivität"
          />
        </span>
      </div>

      {tabelle ? (
        <div className="overflow-x-auto">
          <table className="min-w-[560px] w-full border-collapse text-xs">
            <thead>
              <tr>
                <th className="border-b border-line px-1.5 py-[5px] text-left text-[10.5px] font-semibold text-ink2">
                  Person
                </th>
                {tage.map((t) => (
                  <th
                    key={t}
                    className="border-b border-line px-1.5 py-[5px] text-right text-[10.5px] font-semibold text-ink2"
                  >
                    {tagKurz(t)}
                  </th>
                ))}
                <th className="border-b border-line px-1.5 py-[5px] text-right text-[10.5px] font-semibold text-ink2">
                  Summe
                </th>
              </tr>
            </thead>
            <tbody>
              {personen.map((p) => {
                const weg = abwesend(p.personId)
                return (
                  <tr key={p.personId}>
                    <td className="border-b border-line px-1.5 py-1.5 font-semibold">{p.name}</td>
                    {p.tage.map((t) => (
                      <td
                        key={t.tag}
                        className="tabular border-b border-line px-1.5 py-1.5 text-right"
                      >
                        {/* „ab." statt „–": in der Tabellensicht ist Text der
                          einzige Kanal, und „–" hieße „nichts getan". */}
                        {summe(t.jeQuelle) ||
                          (weg.has(t.tag) ? <span className="text-muted">ab.</span> : '–')}
                      </td>
                    ))}
                    <td className="tabular border-b border-line px-1.5 py-1.5 text-right font-semibold">
                      {p.gesamt}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <div
            className="grid border-b border-line py-1.5 text-[11px] font-semibold text-ink2"
            style={{ gridTemplateColumns: spalten }}
          >
            <div>Person</div>
            {tage.map((t) => (
              <div key={t} className="text-center">
                {kurzeKoepfe ? (t.split('-')[2] ?? '') : tagKurz(t).replace(',', '')}
              </div>
            ))}
          </div>

          {personen.map((person) => {
            const weg = abwesend(person.personId)
            return (
              <div
                key={person.personId}
                className="grid items-center border-b border-line py-2.5 last:border-b-0"
                style={{ gridTemplateColumns: spalten }}
              >
                <div className="truncate pr-2 text-[13px] font-semibold">{person.name}</div>

                {person.tage.map((zelle) => {
                  const gesamt = summe(zelle.jeQuelle)
                  const anteil = gesamt === 0 ? 0 : Math.max(6, (gesamt / hoechst) * 100)
                  const teile = Object.entries(zelle.jeQuelle).filter(([, n]) => n > 0)
                  const abwesendHier = weg.has(zelle.tag)
                  return (
                    <div key={zelle.tag} className="px-[3px]">
                      {gesamt === 0 ? (
                        abwesendHier ? (
                          /**
                           * Leer **und** abwesend: die Schraffur sagt „gilt
                           * nicht" statt „nichts getan". `title` und
                           * `aria-label` tragen dasselbe als Text – ein Muster
                           * allein ist für eine Vorlesehilfe unsichtbar.
                           */
                          <span
                            className="block h-2 rounded"
                            style={{ background: ABWESEND_MUSTER }}
                            title={`${person.name}, ${tagKurz(zelle.tag)}: abwesend`}
                            aria-label={`${person.name}, ${tagKurz(zelle.tag)}: abwesend`}
                            role="img"
                          />
                        ) : (
                          <div className="h-2" aria-hidden />
                        )
                      ) : (
                        <button
                          type="button"
                          onClick={() => onZelleKlick(person.personId, person.name, zelle.tag)}
                          {...anMark(
                            <>
                              <b>{person.name}</b> · {tagKurz(zelle.tag)}
                              <br />
                              {teile
                                .map(
                                  ([q, n]) => `${n} × ${TASK_SOURCE_LABELS[q as TaskSource] ?? q}`,
                                )
                                .join(' · ')}
                              {abwesendHier && (
                                <>
                                  <br />
                                  {/* Kommt vor: halber Tag, oder jemand arbeitet
                                      im Urlaub. Verschweigen wäre die falsche
                                      Auskunft in beide Richtungen. */}
                                  an diesem Tag als abwesend eingetragen
                                </>
                              )}
                              <br />
                              <span className="text-[11px] text-[#cfe0f5]">
                                Klick zeigt alle Ereignisse des Tages
                              </span>
                            </>,
                          )}
                          aria-label={`${person.name}, ${tagKurz(zelle.tag)}: ${gesamt} Ereignisse${abwesendHier ? ', abwesend eingetragen' : ''} – Tag öffnen`}
                          /* Trefferfläche höher als die Markierung – 8-px-Balken
                             allein wäre ein Nadelstich (Skill: ~24 px). */
                          className="flex h-6 w-full cursor-pointer items-center"
                        >
                          <span
                            className="flex h-2 gap-[2px] overflow-hidden rounded"
                            style={{ width: `${anteil}%` }}
                          >
                            {teile.map(([quelle, n]) => (
                              <i
                                key={quelle}
                                className="block h-full first:rounded-l last:rounded-r"
                                style={{
                                  flexGrow: n,
                                  background: QUELLE_FARBE[quelle] ?? 'var(--color-muted)',
                                }}
                              />
                            ))}
                          </span>
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          })}

          {(quellen.length > 1 || hatAbwesenheit) && (
            <div className="mt-3 flex items-center gap-[18px] text-xs text-ink2">
              {quellen.length > 1 &&
                quellen.map((q) => (
                  <span key={q}>
                    <i
                      className="mr-1.5 inline-block size-2.5 rounded-[3px] align-[-1px]"
                      style={{ background: QUELLE_FARBE[q] ?? 'var(--color-muted)' }}
                    />
                    {TASK_SOURCE_LABELS[q as TaskSource] ?? q}
                  </span>
                ))}
              {/* Nur zeigen, wenn im Zeitraum überhaupt jemand weg war – eine
                  Legende für ein Muster, das nicht vorkommt, ist Rauschen. */}
              {hatAbwesenheit && (
                <span>
                  <i
                    className="mr-1.5 inline-block size-2.5 rounded-[3px] align-[-1px]"
                    style={{ background: ABWESEND_MUSTER }}
                  />
                  abwesend
                </span>
              )}
              <span className="ml-auto text-[11px] text-muted">
                Balkenlänge = Ereignisse im Verhältnis zum aktivsten Tag
              </span>
            </div>
          )}
        </>
      )}
      {feld}
    </>
  )
}
